import React from 'react';
import { ChevronDown, FileText, Navigation, Package } from 'lucide-react';
import { GlassCard } from './ui/GlassCard';

type BriefingField = { label: string; value: string; sourceSection: string };
type BriefingCategory = 'overview' | 'venue' | 'technical' | 'crew';

const CATEGORY_TITLES: Record<BriefingCategory, string> = {
  overview: 'Event Overview and Production Instructions',
  venue: 'Venue Access, Parking and Loading',
  technical: 'Technical and Setup Requirements',
  crew: 'Crew Information, Meals and Dress Code',
};

const categoryFor = (section: string, label: string): BriefingCategory => {
  const text = `${section} ${label}`.toLowerCase();
  if (/venue|parking|loading|access|arrival|reporting location|travel|accommodation/.test(text)) return 'venue';
  if (/technical|setup|power|equipment|internet|wi-?fi|booth|floor space/.test(text)) return 'technical';
  if (/crew|meal|catering|dress|uniform|staff/.test(text)) return 'crew';
  return 'overview';
};

const usefulValue = (value: unknown) => value !== '' && value !== null && value !== undefined;

export function buildCrewBriefingGroups(blueprint: any) {
  const catalog:Record<string,[string,string]>={
    'log-1':['Venue Contact Name (Who do we report to on the day?)','1. Venue & Access Logistics'],
    'log-2':['Venue Contact Phone Number','1. Venue & Access Logistics'],
    'log-3':['Loading Access','1. Venue & Access Logistics'],
    'log-4':['Crew Parking','1. Venue & Access Logistics'],
    'tech-1':['Power Supply','2. Setup & Technical Requirements'],
    'tech-2':['Setup Environment','2. Setup & Technical Requirements'],
    'tech-3':['Wi-Fi Access','2. Setup & Technical Requirements'],
    'ops-2':['Crew Meals','3. Crew & Event Operations'],
    'ops-3':['Crew Dress Code','3. Crew & Event Operations'],
    'contact':['On-the-Night Contact Person','Event Logistics'],
    'access':['Venue Access & Pack-in Time','Event Logistics'],
    'parking':['Loading Dock & Parking Instructions','Event Logistics'],
    'power':['Power & Wi-Fi Availability','Event Logistics'],
    'wifi_details':['Wi-Fi Network & Password','Event Logistics'],
    'meals':['Contractor Meals','Event Logistics'],
  };
  const groups:Record<BriefingCategory,BriefingField[]>={overview:[],venue:[],technical:[],crew:[]};
  for(const field of Array.isArray(blueprint?.structure)?blueprint.structure:[]){
    const trusted=catalog[field.id];if(!trusted||field.label!==trusted[0]||field.section!==trusted[1])continue;
    const value=blueprint?.data?.[field.section+': '+field.label];
    if(!usefulValue(value)||!['string','number','boolean'].includes(typeof value)||/(https?:\/\/|storage:\/\/|www\.)/i.test(String(value)))continue;
    groups[categoryFor(field.section,field.label)].push({label:field.label,value:String(value),sourceSection:field.section});
  }
  const documents:Array<{label:string;url:string}>=[];
  for(const doc of Array.isArray(blueprint?.equipmentDocuments)?blueprint.equipmentDocuments:[]){
    if(doc.source!=='EQUIPMENT_MANAGER'||!doc.productId||!doc.documentId)continue;
    try{const url=new URL(doc.url);if(url.protocol==='https:'&&!url.username&&!url.password)documents.push({label:String(doc.name||'Equipment document'),url:url.href});}catch{}
  }

  return { groups, documents };
}

const BriefingSection = ({ title, fields, children }: {
  title: string;
  fields?: BriefingField[];
  children?: React.ReactNode;
}) => (
  <details className="group rounded-2xl border border-white/10 bg-slate-900/60">
    <summary className="flex min-h-[52px] cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-bold text-white">
      {title}
      <ChevronDown size={17} className="shrink-0 text-slate-500 transition-transform group-open:rotate-180" />
    </summary>
    <div className="space-y-4 border-t border-white/5 px-4 py-4">
      {fields?.length ? fields.map((field, index) => (
        <div key={`${field.sourceSection}-${field.label}-${index}`}>
          <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">{field.label}</p>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-200">{field.value}</p>
        </div>
      )) : null}
      {children}
      {!fields?.length && !children && <p className="text-sm italic text-slate-500">No information supplied.</p>}
    </div>
  </details>
);

export function CrewEventBriefing({
  job,
  blueprint,
  loading,
  onDocumentOpen,
}: {
  job: { shift_notes?: string; travel_arrangements?: string;operationalNotes?:Array<{label:string;value:string}> };
  blueprint: any;
  loading: boolean;
  onDocumentOpen: (event: React.MouseEvent, url: string, filename: string) => void;
}) {
  const { groups, documents } = buildCrewBriefingGroups(blueprint);
  const hasBlueprint = Object.values(groups).some(fields => fields.length > 0) || documents.length > 0;

  return (
    <div className="space-y-4 animate-fade-in-up">
      <div className="px-1">
        <h3 className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em] text-brand-neonPink">
          <FileText size={14} /> Operational Event Briefing
        </h3>
        <p className="mt-2 text-sm text-slate-400">Open only the operational section you need.</p>
      </div>

      {loading ? (
        <div role="status" className="rounded-2xl border border-brand-neonPink/20 bg-brand-neonPink/5 p-8 text-center text-xs font-bold uppercase tracking-widest text-brand-neonPink">
          Loading operational briefing…
        </div>
      ) : (
        <>
          {job.operationalNotes?.map((note,i)=><p key={i}>{note.label}: {note.value}</p>)}
          <BriefingSection title={CATEGORY_TITLES.overview} fields={groups.overview}>
            {job.shift_notes && (
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Shift briefing and operational notes</p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-200">{job.shift_notes}</p>
              </div>
            )}
          </BriefingSection>
          <BriefingSection title={CATEGORY_TITLES.venue} fields={groups.venue}>
            {job.travel_arrangements && (
              <div>
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">Travel and accommodation</p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-200">{job.travel_arrangements}</p>
              </div>
            )}
          </BriefingSection>
          <BriefingSection title={CATEGORY_TITLES.technical} fields={groups.technical}>

          </BriefingSection>
          <BriefingSection title={CATEGORY_TITLES.crew} fields={groups.crew} />
          <BriefingSection title="Approved Equipment Guides and Manuals">
            {documents.length > 0 ? documents.map((document, index) => (
              <a
                key={`${document.label}-${index}`}
                href={document.url}
                onClick={event => onDocumentOpen(event, document.url, `quikpix-event-document-${index + 1}.pdf`)}
                className="flex min-h-[48px] items-center justify-between rounded-xl border border-brand-neonPink/30 bg-brand-neonPink/10 px-4 text-sm font-bold text-brand-neonPink"
              >
                <span className="flex items-center gap-2"><FileText size={16} /> {document.label}</span>
                <Navigation size={15} />
              </a>
            )) : <p className="text-sm italic text-slate-500">No approved equipment documents supplied.</p>}
          </BriefingSection>
          {!hasBlueprint && (
            <GlassCard className="rounded-2xl p-5 text-center">
              <Package size={22} className="mx-auto text-slate-600" />
              <p className="mt-2 text-sm text-slate-400">No approved operational information is available yet.</p>
            </GlassCard>
          )}
        </>
      )}
    </div>
  );
}
